import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Download, Trash2, FileText, FileImage, FileSpreadsheet, File, Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { api, errMsg, fmtBytes, fmtDateTime } from "@/lib/api";
import { usePaged } from "@/lib/hooks";
import { Pager } from "@/components/core/Pager";
import { useSelection, SelectAll, SelectOne, BulkBar } from "@/components/core/BulkActions";
import { Panel, EmptyState } from "@/components/core/AppLayout";
import { AttachmentsPanel } from "@/components/core/AttachmentsPanel";
import { ConfirmDialog } from "@/components/core/ConfirmDialog";

const MAX = 25 * 1024 * 1024;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.svg,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.txt,.csv,.rtf,.zip";

const iconFor = (ct = "") => (ct.startsWith("image/") ? FileImage : ct.includes("pdf") || ct.includes("word") ? FileText : ct.includes("sheet") || ct.includes("excel") ? FileSpreadsheet : File);

export const ClientDocuments = ({ clientId }) => {

  const selection = useSelection();
  const [progress, setProgress] = useState(null);
  const [drag, setDrag] = useState(false);
  const [del, setDel] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const inputRef = useRef();
  const [src, setSrc] = useState("tutti");
  const paged = usePaged(`/clients/${clientId}/documents`, src === "tutti" ? {} : { source: src }, 25);
  const { items: docs, reload } = paged;
  const shown = docs || [];

  const upload = async (files) => {
    for (const file of files) {
      if (file.size > MAX) { toast.error(`«${file.name}» supera la dimensione massima di 25 MB`); continue; }
      const fd = new FormData();
      fd.append("file", file);
      setProgress({ name: file.name, pct: 0 });
      try {
        await api.post(`/clients/${clientId}/documents`, fd, {
          onUploadProgress: (e) => setProgress({ name: file.name, pct: Math.round((e.loaded / (e.total || 1)) * 100) }),
        });
        toast.success(`«${file.name}» caricato`);
      } catch (e) { toast.error(errMsg(e, "Caricamento non riuscito")); }
    }
    setProgress(null);
    reload();
  };

  const download = async (d) => {
    setDownloading(d.id);
    try {
      const r = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.nome; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error("Download non riuscito"); }
    setDownloading(null);
  };

  const remove = async () => {
    try { await api.delete(`/documents/${del.id}`); toast.success("Documento eliminato"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  return (
    <div className="space-y-6" data-testid="client-documents">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload([...e.dataTransfer.files]); }}
        onClick={() => !progress && inputRef.current.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed px-8 py-10 text-center transition-colors duration-200 ${drag ? "border-[#E8173A] bg-red-50/50" : "border-[#0A1128]/20 bg-[#A6DAFC] hover:border-[#0A1128]/50"}`}
        data-testid="document-dropzone"
      >
        <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="document-file-input"
          onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} />
        {progress ? (
          <div className="max-w-sm mx-auto" data-testid="document-upload-progress">
            <Loader2 className="h-7 w-7 animate-spin text-[#0A1128] mx-auto mb-3" />
            <div className="text-sm font-bold text-slate-700 mb-2 truncate">Caricamento di «{progress.name}»...</div>
            <Progress value={progress.pct} className="h-2" />
          </div>
        ) : (
          <>
            <div className="h-14 w-14 rounded-2xl bg-[#0A1128] text-white flex items-center justify-center mx-auto mb-3"><UploadCloud className="h-6 w-6" /></div>
            <div className="font-display text-lg font-extrabold text-[#0A1128]">Trascina qui i file o clicca per caricarli</div>
            <div className="text-xs text-slate-500 mt-1">PDF, immagini, documenti Office · max 25 MB per file</div>
          </>
        )}
      </div>

      <div className="flex gap-2" data-testid="documents-filter">
        {[["tutti", "Tutti"], ["cliente", "Caricati dal cliente"], ["socio", "Caricati dai soci"]].map(([k, l]) => (
          <button key={k} onClick={() => setSrc(k)} className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${src === k ? "bg-[#0A1128] text-white" : "bg-[#A6DAFC] text-[#0A1128] hover:bg-[#8FD0FB]"}`} data-testid={`documents-filter-${k}`}>{l}</button>
        ))}
      </div>

      <BulkBar tipo="documenti" selection={selection} onDone={reload} />
      <Panel className="overflow-hidden">
        {docs && !shown.length ? <EmptyState icon={FileText} title="Nessun documento" text="Carica contratti, brief, loghi e materiali del cliente." testid="documents-empty" /> : (
          <Table data-testid="documents-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10"><SelectAll ids={shown.map((d) => d.id)} selection={selection} testid="documents-select-all" /></TableHead>
                {["Nome", "Dimensione", "Caricato da", "Data", ""].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wider">{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((d) => {
                const I = iconFor(d.content_type);
                return (
                  <TableRow key={d.id} data-testid={`document-row-${d.id}`}>
                    <TableCell><SelectOne id={d.id} selection={selection} testid={`document-select-${d.id}`} /></TableCell>
                    <TableCell><div className="flex items-center gap-3"><div className="h-9 w-9 rounded-lg bg-[#A6DAFC] flex items-center justify-center"><I className="h-4 w-4 text-[#0A1128]" /></div><div className="min-w-0"><div className="flex items-center gap-2"><span className="font-bold text-sm text-slate-900">{d.nome}</span>
                      {d.source === "cliente" && <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#E8173A] text-white px-2 py-0.5" data-testid={`document-client-tag-${d.id}`}>Caricato dal cliente</span>}</div>
                      {d.nota && <div className="text-xs text-slate-600 mt-0.5">Nota: {d.nota}</div>}</div></div></TableCell>
                    <TableCell className="text-sm text-slate-600">{fmtBytes(d.size)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{d.uploader_nome || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{fmtDateTime(d.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => download(d)} className="p-2 rounded-lg hover:bg-[#A6DAFC] text-[#0A1128]" data-testid={`document-download-${d.id}`}>
                          {downloading === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        </button>
                        <button onClick={() => setDel(d)} className="p-2 rounded-lg hover:bg-red-50 text-[#E8173A]" data-testid={`document-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        <Pager data={paged.data} page={paged.page} setPage={paged.setPage} testid="documents-pager" />
      </Panel>
      <Panel className="mt-5 p-5" data-testid="docs-ped-files"><AttachmentsPanel tipo="ped" clientId={clientId} readOnly ped title="File del PED" testid="docs-ped-panel" /></Panel>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare il documento?" description={`«${del?.nome}» verrà rimosso dalla scheda cliente.`} onConfirm={remove} />
    </div>
  );
};
